/* DeviceConfigurationSnapshotViewComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./DeviceConfigurationSnapshotViewRenderer");
const DeviceActionsColumn = require("./column_renderers/device_actions/DeviceActionsComponent");
const TypeColumn = require("./column_renderers/Type/TypeComponent");
const DateColumn = require("./column_renderers/date/DateComponent");
const DefaultSnapshotColumn = require("./column_renderers/default-snapshot/DefaultSnapshotComponent");
// const RequestedOnCol = require("./column_renderers/requested_on/RequestedOnComponent");
// const RequestedByCol = require("./column_renderers/requested_by/RequestedByComponent");
// const RequestedFromCol = require("./column_renderers/requested_from/RequestedFromComponent");
const _ = require("lodash");

class DeviceConfigurationSnapshotViewComponent extends EventEmitter {
    constructor(logger, gameEditor, DeviceConfigEntity, userSession, muf, storageFactory) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._DeviceConfigEntity = DeviceConfigEntity;
        this._muf = muf;
        this._storageFactory = storageFactory;
        this._userGridData = undefined;

        //TODO - When filter is available, we can uncomment this.
        //this._gameEditor.setFilterProperties(DeviceConfigEntity.$filterProperties);

        this._userSession = userSession;

        //DATA MODEL
        this._deviceConfigFeed = undefined;
        this._deviceConfigCollection = undefined;

        //VIEW MODEL
        this._defaultSnapshot = {};

        this._allSnapshots = {};

        this.columnConfig = {
            isStableDeviceConfiguration: {
                label: "Default",
                width: 65,
                priority: 0,
                columnRendererComponent: DefaultSnapshotColumn,
                sortable: false,
            },
            wasDifferentAtCaptureTime: {
                label: "Type",
                width: 50,
                priority: 1,
                columnRendererComponent: TypeColumn,
            },
            description: {
                label: "Name",
                width: 300,
                priority: 2,
            },
            capturedTime: {
                label: "Captured on",
                priority: 3,
                width: 180,
                columnRendererComponent: DateColumn,
            },
            initiatorUser: {
                label: "Requested by",
                priority: 4,
                width: 200,
            },
            startTime: {
                label: "Requested on",
                priority: 5,
                width: 180,
                columnRendererComponent: DateColumn,
            },
            initiatorIp: {
                label: "Requested from",
                priority: 6,
                width: 170,
            },

            deviceActions: {
                label: "Actions",
                columnRendererComponent: DeviceActionsColumn,
                sortable: false,
                entityColumn: true,
                width: 190,
                context: {
                    isRestoreRunAvailable:
                        this._gameEditor.canRestoreBackup &&
                        this._gameEditor.isBackupActionAvailable,
                },
            },
        };

        this._onDeviceConfigChangeFeedInitialized =
            this._onDeviceConfigChangeFeedInitialized.bind(this);

        this._renderer = new Renderer(this);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        this._gameEditor._device.on(
            "PropertiesUpdated",
            (this._onEntityPropertiesUpdated = this._onEntityPropertiesUpdated.bind(this))
        );
    }

    _unregisterListeners() {
        if (this._gameEditor._device) {
            this._gameEditor._device.removeAllListeners("PropertiesUpdated");
        }
    }

    // All device instance and config feed listeners reset the grid view (_onDeviceConfigChangeFeedInitialized)
    _registerDeviceInstanceConfigListeners() {
        this._gameEditor.on(
            "DeviceConfigChangeFeedInitialized",
            this._onDeviceConfigChangeFeedInitialized
        );
    }

    _unregisterDeviceInstanceConfigListeners() {
        this._gameEditor.removeListener(
            "DeviceConfigChangeFeedInitialized",
            this._onDeviceConfigChangeFeedInitialized
        );
    }

    _registerConfigFeedListeners() {
        this._deviceConfigFeed.on("EntityAdded", this._onDeviceConfigChangeFeedInitialized);
        this._deviceConfigFeed.on(
            "EntityPropertiesUpdated",
            this._onDeviceConfigChangeFeedInitialized
        );
    }
    _unregisterConfigFeedListeners() {
        if (this._deviceConfigFeed) {
            this._deviceConfigFeed.removeListener(
                "EntityAdded",
                this._onDeviceConfigChangeFeedInitialized
            );
            this._deviceConfigFeed.removeListener(
                "EntityPropertiesUpdated",
                this._onDeviceConfigChangeFeedInitialized
            );
        }
    }

    _onDeviceConfigChangeFeedInitialized() {
        this._defaultSnapshot = {};
        this._allSnapshots = {};
        this._deviceConfigCollection = this._deviceConfigFeed.cache;
        this._buildViewModel(this._deviceConfigCollection);
        this.emit("DeviceConfReset");
    }

    _onEntityPropertiesUpdated(newProperties, oldProperties) {
        const newStatus = newProperties.discoveryStatus,
            oldStatus = oldProperties.discoveryStatus;

        if (
            (this.testCapturing(newStatus) && !this.testCapturing(oldStatus)) ||
            (!this.testCapturing(newStatus) && this.testCapturing(oldStatus))
        ) {
            this.emit("DeviceCaptureChange");
        }
    }

    whenModelIsReady() {
        return this._gameEditor
            .whenDeviceIsReady()
            .then(() => {
                this._registerListeners();
                _.set(
                    this.columnConfig,
                    "deviceActions.context.isOsSupported",
                    this._gameEditor._osIsSupported
                );

                this._pagingFeed = this._gameEditor._pagingFeed;
                this._pagingFeed.setColumnConfig(this.columnConfig, [
                    "updatedBy",
                    "createdBy",
                    "updatedOn",
                    "createdOn",
                    "updatedIp",
                    "createdIp",
                    "backupConfigurationId",
                ]);
                this._gridKey =
                    "snapshot_grid_" + this._gameEditor._device.getName().replace(/ /g, "_");
                this._userGridData = this._storageFactory.getRemoteStorageInstanceForEntityGrid(
                    "entity_grid_gl",
                    this._gridKey
                );
                if (!_.isEmpty(this._userGridData.getSorting())) {
                    this._pagingFeed.setSort(
                        this._patchSorting(this._userGridData.getSorting()) || {}
                    );
                } else {
                    this._pagingFeed.setSort(this._patchSorting(this._pagingFeed.getSort()) || {});
                }
                return this._gameEditor.whenDeviceConfigIsReady();
            })
            .then(() => {
                this._deviceConfigFeed = this._gameEditor.getDeviceConfig();
                this._registerDeviceInstanceConfigListeners();
                this._registerConfigFeedListeners();
                return this._deviceConfigFeed.whenInitialized();
            })
            .then(() => {
                this._deviceConfigCollection = this._deviceConfigFeed.cache;
                this._buildViewModel(this._deviceConfigCollection);
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "DeviceConfigurationSnapshotViewComponent.whenModelIsReady error."
                );
                return Promise.reject(err);
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterDeviceInstanceConfigListeners();
        this._unregisterConfigFeedListeners();
        this._unregisterListeners();
        return this._renderer.shutdown().then(
            () => {
                this._deviceConfigFeed = undefined;
                this._deviceConfigCollection = undefined;
                this._DeviceConfigEntity = undefined;
                this._logger = undefined;
                this._gameEditor = undefined;
                this._renderer = undefined;
                this._defaultSnapshot = undefined;
                this._allSnapshots = undefined;
            },
            (err) => {
                this._logger.warn(
                    {error: err},
                    "DeviceConfigurationSnapshotViewComponent.shutdown() error."
                );
            }
        );
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get logger() {
        return this._logger;
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onFilterChanged(filterObj) {
        this._logger.debug("Filter has changed! Do something with this: ", filterObj);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _buildViewModel(cache) {
        Object.keys(cache).forEach((id) => {
            let marshalledObj = this._marshallDeviceData(cache[id]);
            if (cache[id].isStableDeviceConfiguration) {
                this._defaultSnapshot[id] = cache[id];
            } else {
                this._allSnapshots[id] = marshalledObj;
            }
        });
    }

    _marshallDeviceData(devConf) {
        let marshalledObj = {
            id: devConf.id,
            activeSnapshotIndicator: {
                active: devConf.isStableDeviceConfiguration,
                description: devConf.description,
            },
            snapshot: {
                snapshot: "Unknown",
                wasDifferentAtCaptureTime: devConf.wasDifferentAtCaptureTime,
            },
            deviceActions: {
                view: true,
                edit: true,
                compare: true,
            },
        };

        let timestamp = devConf.startTime
            ? devConf.startTime
            : devConf.capturedTime
            ? devConf.capturedTime
            : undefined;

        if (timestamp) {
            let date = new Date(timestamp);
            let datelocale = date.toLocaleDateString();
            let timelocale = date.toLocaleTimeString();
            let fullTimeString = datelocale + " " + timelocale;
            marshalledObj.snapshot.snapshot = fullTimeString;
        }

        if (!devConf.isStableDeviceConfiguration) {
            marshalledObj.deviceActions.delete = true;
            marshalledObj.deviceActions.compare = true;
            marshalledObj.deviceActions.defaultSnapshot = true;
        } else {
            if (_.isEmpty(marshalledObj.activeSnapshotIndicator.description)) {
                marshalledObj.activeSnapshotIndicator.description = `Default Configuration Snapshot`;
            } else {
                marshalledObj.activeSnapshotIndicator.description = `Default Configuration Snapshot - ${marshalledObj.activeSnapshotIndicator.description}`;
            }
        }

        return marshalledObj;
    }

    onUserSorted(sortData) {
        sortData = [].concat(sortData);
        let sort = {};
        sort = this._patchSorting(sort);
        sortData.forEach((sortData) => {
            if (!sortData || !sortData.sortColumn || !this.columnConfig[sortData.sortColumn]) {
                return;
            }
            const sortCriteria =
                this.columnConfig[sortData.sortColumn].valuePath ||
                this.columnConfig[sortData.sortColumn].path ||
                sortData.sortColumn;
            sort[sortCriteria] = sortData.sort === "asc" ? 1 : -1;
        });

        this._pagingFeed.setSort(sort);
    }

    _patchSorting(sorting) {
        sorting = Object.assign({}, {isStableDeviceConfiguration: -1}, sorting);
        return sorting;
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    getDefaultSnapshot() {
        return this._defaultSnapshot;
    }

    getAllSnapshots() {
        return this._allSnapshots;
    }

    deleteSnapshot(id) {
        return new Promise((resolve /*,reject*/) => {
            this._deviceConfigCollection[id].runDeleteDeviceConfigWork();
            resolve();
        });
    }

    setNewDefaultSnapshot(id) {
        return this._deviceConfigCollection[id].runSetAsStableConfigWork();
    }

    testCapturing(status) {
        return status === "PENDING" || status === "QUEUED" || status === "RUNNING";
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = DeviceConfigurationSnapshotViewComponent;

DeviceConfigurationSnapshotViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.entities.DeviceConfiguration",
    "$teve.userSession",
    "$teve.ui.userFeedback",
    "$teve.storageFactory",
];

DeviceConfigurationSnapshotViewComponent.$displayDetails = {
    type: "View",
    name: "Snapshots",
    icon: "configdrift.snapshots",
    shown: true,
    title: "Snapshots",
    order: 3,
};
